import React from "react";
import {
  DepressionContentPage,
  FAQItem,
} from "@/components/DepressionContentPage";
import { RelatedLink } from "@/components/RelatedPages";
import { buildMedicalClinicLocal, Crumb } from "@/components/schemaConstants";
import CrisisLifelineBanner from "@/components/CrisisLifelineBanner";

/**
 * /senior-depression-treatment
 *
 * Depression care for adults 65+ in Coral Springs. In-network claim is limited
 * to Medicare and the VA. No guarantees; eligibility decided at consultation.
 * SAINT® is for adults 22+ and is never implied for anyone under 22 (not
 * relevant here, but the rule is preserved). Ends with the 988 crisis line.
 */
const PHONE = "954-903-1323";

const faqs: FAQItem[] = [
  {
    question: "Is depression a normal part of aging?",
    answer:
      "No. Depression is not a normal part of getting older, and it is treatable at any age. Many older adults feel better with the right combination of therapy, medication management, or non-drug options like TMS. Because depression in older adults is often missed, the first step is a careful evaluation.",
  },
  {
    question: "Does Medicare cover TMS?",
    answer:
      "Medicare Part B can cover TMS therapy for major depression when medical-necessity criteria are met. Neuro Wellness is an in-network Medicare provider, and we verify your benefits for free before your first visit. Exact coverage depends on your plan; we confirm it for you.",
  },
  {
    question: "Can I do TMS if I take several medications?",
    answer:
      "TMS is medication-free, so it does not add a new drug or new drug interactions to your current prescriptions. Your physician reviews every medication you take and decides what is appropriate for you after an evaluation.",
  },
  {
    question: "Is TMS safe for older adults?",
    answer:
      "TMS is a non-invasive, outpatient treatment with no anesthesia or sedation — you drive yourself to and from each session. Individual results vary, and your physician decides whether TMS is appropriate for you after an evaluation.",
  },
  {
    question: "Can my daughter or son come with me?",
    answer:
      "Yes. Family and caregivers are welcome. We have companion seating, and your adult child can call us on your behalf. Because you are an adult, we will need your consent to share any clinical details, and we will walk you both through it.",
  },
  {
    question: "Do I need a referral?",
    answer:
      "A referral is not required to book a free consultation. You can call us directly. If you have a current psychiatrist or primary care doctor, we are happy to coordinate with them with your permission.",
  },
];

const relatedLinks: RelatedLink[] = [
  {
    title: "TMS Therapy",
    href: "/services/tms",
    description: "Medication-free magnetic stimulation for depression.",
  },
  {
    title: "SPRAVATO®",
    href: "/services/spravato",
    description: "FDA-approved nasal spray for treatment-resistant depression.",
  },
  {
    title: "Medication Management",
    href: "/services/medication-management",
    description: "A careful review of every current prescription.",
  },
  {
    title: "Does Medicare Cover TMS?",
    href: "/medicare-tms-coverage",
    description:
      "How Medicare coverage for TMS works and our free benefits check.",
  },
];

const breadcrumbs: Crumb[] = [
  { name: "Home", href: "/" },
  { name: "Depression Treatment", href: "/depression-treatment-coral-springs" },
  { name: "Seniors & Older Adults", href: "/senior-depression-treatment" },
];

const SeniorDepressionTreatment: React.FC = () => {
  return (
    <>
      <DepressionContentPage
        seo={{
          title:
            "Depression Treatment for Seniors & Older Adults in Coral Springs, FL | Medicare Accepted",
          description:
            "Outpatient depression care for adults 65+ in Coral Springs: TMS, SPRAVATO®, therapy and medication management. In-network with Medicare. Free consultation.",
          canonical: "/senior-depression-treatment",
          keywords:
            "senior depression treatment Coral Springs, older adult depression Florida, Medicare TMS depression, depression treatment 65+, geriatric depression care Coral Springs",
          breadcrumbs,
          extraSchema: [buildMedicalClinicLocal()],
        }}
        badge="Coral Springs, FL · Medicare In-Network"
        heroTitle="Depression Treatment for Seniors and Older Adults in Coral Springs, FL"
        heroSubtitle="Depression is not a normal part of aging, and it's treatable at any age. We're in-network with Medicare."
        heroCtaPrimary="Book a Free Consultation"
        heroCtaPrimaryHref="/#consultation"
        reviewedBy="Daniel Pietras, MD"
        lastUpdated="2026-11-04"
        sections={[
          {
            heading: "Depression later in life looks different",
            variant: "text",
            body: (
              <>
                <p>
                  In older adults, depression often shows up as fatigue,
                  withdrawal from friends and family, irritability, poor sleep,
                  or memory and concentration complaints — rather than sadness
                  or crying. Many seniors describe a loss of interest in things
                  they used to enjoy, or a sense of being a burden.
                </p>
                <p>
                  Because these symptoms can look like other health conditions
                  or even &ldquo;just getting older,&rdquo; depression in older
                  adults is often missed by patients, families, and even
                  providers. It is not a character flaw or a weakness, and it is
                  not something you simply have to live with. It is a treatable
                  medical condition — and treatment can help at any age.
                </p>
              </>
            ),
          },
          {
            heading: "Treatment options for older adults",
            variant: "cards",
            cards: [
              {
                title: "TMS Therapy",
                body: "Medication-free magnetic stimulation. No anesthesia, you drive yourself home, and each session is about 20 minutes. Individual results vary; eligibility decided at consultation.",
                icon: "brain",
              },
              {
                title: "SPRAVATO® (Esketamine)",
                body: "FDA-approved nasal spray for adults with treatment-resistant depression, given in-office with monitoring.",
                icon: "spray",
              },
              {
                title: "Medication Management",
                body: "A careful review of every current prescription for interactions, side effects, and whether each is still the right choice.",
                icon: "pill",
              },
              {
                title: "Individual & Group Therapy",
                body: "Talk therapy tailored to the concerns older adults bring — loss, isolation, chronic illness, and life transitions.",
                icon: "talk",
              },
            ],
          },
          {
            heading: "Why TMS is often a good fit for seniors",
            variant: "text",
            body: (
              <>
                <p>
                  Many older adults are already taking several medications for
                  other health conditions. Because TMS is medication-free, it
                  adds no new drug and no new drug interactions to your current
                  list. There are no systemic side effects like the weight gain,
                  drowsiness, or dry mouth that can come with some
                  antidepressants.
                </p>
                <p>
                  TMS is also outpatient — there is no sedation and no recovery
                  time, so you can keep your daily routine and drive yourself to
                  and from each short session. Individual results vary, and your
                  physician decides what&rsquo;s appropriate after an
                  evaluation.
                </p>
              </>
            ),
          },
          {
            heading: "Medicare coverage",
            variant: "text",
            body: (
              <>
                <p>
                  Neuro Wellness is an in-network Medicare provider. TMS and
                  SPRAVATO® may be covered when Medicare criteria are met, and
                  we verify your benefits for free before your first visit so
                  there are no surprises.
                </p>
                <p>
                  Learn more about how Medicare coverage works for these
                  treatments:{" "}
                  <a
                    href="/medicare-tms-coverage"
                    className="text-accent font-semibold underline underline-offset-4"
                  >
                    Does Medicare Cover TMS?
                  </a>{" "}
                  and{" "}
                  <a
                    href="/medicare-spravato-coverage"
                    className="text-accent font-semibold underline underline-offset-4"
                  >
                    Medicare & SPRAVATO®
                  </a>
                  .
                </p>
              </>
            ),
          },
          {
            heading: "For adult children and caregivers",
            variant: "text",
            body: (
              <>
                <p>
                  You can call us on your parent&rsquo;s behalf to ask questions
                  and learn about the options. If you are unsure how to bring up
                  treatment, we can help you think through what to say. Because
                  your parent is an adult, we will need their consent to share
                  clinical details, and we will walk you both through that
                  process.
                </p>
                <p>
                  Practical tips: companion seating is available, and we can
                  discuss scheduling around transportation and other
                  appointments. See our guide for families at{" "}
                  <a
                    href="/help-for-elderly-parent-with-depression"
                    className="text-accent font-semibold underline underline-offset-4"
                  >
                    helping an elderly parent with depression
                  </a>
                  .
                </p>
              </>
            ),
          },
          {
            heading: "What your first visit looks like",
            variant: "steps",
            steps: [
              {
                title: "Free consultation",
                body: "A no-pressure conversation about symptoms, history, and goals — by phone or in person at our Coral Springs clinic.",
              },
              {
                title: "Evaluation and benefits check",
                body: "A physician evaluation, a review of your current medications, and a free verification of your Medicare and any secondary benefits.",
              },
              {
                title: "Personalized plan",
                body: "A clear plan — therapy, medication management, TMS, SPRAVATO®, or a combination — with your costs explained up front.",
              },
            ],
          },
        ]}
        faq={faqs}
        faqHeading="Frequently Asked Questions"
        relatedLinks={relatedLinks}
        relatedHeading="Related Resources"
        finalCtaHeading="Caring for depression at any age"
        finalCtaText={`Call our Coral Springs team at ${PHONE} to book a free consultation, or verify your Medicare benefits at no cost. Eligibility is decided at the consultation; individual results vary.`}
        finalCtaPrimaryLabel="Book a Free Consultation"
        finalCtaPrimaryHref="/#consultation"
        finalCtaSecondaryLabel="Verify My Insurance — Free"
        finalCtaSecondaryHref="tel:9549031323"
      />
      <div className="container-px mx-auto max-w-3xl px-4 pb-12">
        <CrisisLifelineBanner />
      </div>
    </>
  );
};

export default SeniorDepressionTreatment;
